import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Card, CardContent } from '@/shared/components/ui/card';
import { AlertCircle, CreditCard, Calendar, DollarSign } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Invoice } from '../types/subscription-types';

interface PendingPaymentModalProps {
  isOpen: boolean;
  invoice: Invoice;
  onPay: () => void;
  onContinueLimited: () => void;
}

export const PendingPaymentModal = ({ 
  isOpen, 
  invoice, 
  onPay, 
  onContinueLimited 
}: PendingPaymentModalProps) => {
  const { t, i18n } = useTranslation();

  // Map i18n language to locale format
  const getLocale = () => {
    const lang = i18n.language;
    if (lang.startsWith('pt')) return 'pt-BR';
    if (lang.startsWith('en')) return 'en-US';
    return 'en-US'; // Default fallback
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat(getLocale(), {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(getLocale(), {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="sm:max-w-[500px]" onInteractOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-amber-100 dark:bg-amber-900/20 rounded-full">
              <AlertCircle className="h-6 w-6 text-amber-600 dark:text-amber-500" />
            </div>
            <DialogTitle className="text-2xl">
              {t('subscription.payment.title', { defaultValue: 'Complete seu pagamento' })}
            </DialogTitle>
          </div>
          <DialogDescription className="pt-2">
            {t('subscription.payment.description', { 
              defaultValue: 'Para ativar todos os recursos do seu plano, é necessário realizar o pagamento.' 
            })}
          </DialogDescription>
        </DialogHeader>

        <Card className="border-2">
          <CardContent className="pt-6 space-y-4">
            {/* Invoice Number */}
            <div className="flex justify-between items-center pb-3 border-b">
              <span className="text-sm text-muted-foreground">
                {t('subscription.payment.invoiceNumber', { defaultValue: 'Número da Fatura' })}
              </span>
              <span className="font-mono font-semibold">{invoice.invoice_number}</span>
            </div>

            {/* Amount */}
            <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium">
                  {t('subscription.payment.amount', { defaultValue: 'Valor' })}
                </span>
              </div>
              <span className="text-2xl font-bold text-primary">
                {formatCurrency(invoice.amount, invoice.currency)}
              </span>
            </div>

            {/* Due Date */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">
                  {t('subscription.payment.dueDate', { defaultValue: 'Vencimento' })}
                </span>
              </div>
              <span className="font-semibold">{invoice.due_date ? formatDate(invoice.due_date) : '—'}</span>
            </div>
          </CardContent>
        </Card>

        <DialogFooter className="flex-col sm:flex-col gap-2">
          <Button 
            onClick={onPay} 
            className="w-full gap-2"
            size="lg"
          >
            <CreditCard className="h-4 w-4" />
            {t('subscription.payment.payNow', { defaultValue: 'Pagar Agora' })}
          </Button>
          
          <Button 
            onClick={onContinueLimited} 
            variant="ghost" 
            className="w-full"
          >
            {t('subscription.payment.continueLimited', { 
              defaultValue: 'Acessar com recursos limitados' 
            })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
